import React, { useEffect, useState } from 'react';
import { vault } from '../../services/api';
import { useToast } from '../../context/ToastContext';

interface VaultUnlockModalProps {
  onUnlock: () => void;
  onClose: () => void;
}

const VaultUnlockModal: React.FC<VaultUnlockModalProps> = ({ onUnlock, onClose }) => {
  const { toast } = useToast();
  const [pinSet, setPinSet] = useState<boolean | null>(null);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    vault.pinStatus().then(r => setPinSet(r.data.pin_set)).catch(() => setPinSet(true));
  }, []);

  const handleSetPin = async () => {
    if (pin.length < 6) { toast('PIN must be at least 6 characters', 'error'); return; }
    if (pin !== confirmPin) { toast('PINs do not match', 'error'); return; }
    setLoading(true);
    try {
      await vault.setPin(pin);
      await vault.unlock(pin);
      onUnlock();
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Unable to set PIN', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    if (!pin) { toast('Enter your vault PIN', 'error'); return; }
    setLoading(true);
    try {
      await vault.unlock(pin);
      onUnlock();
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Incorrect PIN', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (pin.length < 6) { toast('New PIN must be at least 6 characters', 'error'); return; }
    if (pin !== confirmPin) { toast('PINs do not match', 'error'); return; }
    setLoading(true);
    try {
      await vault.resetPin(loginPassword, pin);
      await vault.unlock(pin);
      onUnlock();
    } catch (e: any) {
      toast(e?.response?.data?.detail || 'Unable to reset PIN', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (pinSet === null) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 28, maxWidth: 380, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>
          {showReset ? 'Reset Vault PIN' : pinSet ? 'Unlock Vault' : 'Set Your Vault PIN'}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 18, lineHeight: 1.5 }}>
          {showReset
            ? 'Confirm your login password, then choose a new vault PIN.'
            : pinSet
              ? 'Enter your vault PIN to reveal sensitive information.'
              : 'This PIN protects reveals of sensitive vault data. It is separate from your login password.'}
        </div>

        {showReset && (
          <input type="password" placeholder="Login password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} style={inputStyle} />
        )}
        <input type="password" placeholder={showReset || !pinSet ? 'New PIN (min 6 characters)' : 'Vault PIN'} value={pin} onChange={e => setPin(e.target.value)} style={{ ...inputStyle, marginTop: showReset ? 10 : 0 }} />
        {(showReset || !pinSet) && (
          <input type="password" placeholder="Confirm PIN" value={confirmPin} onChange={e => setConfirmPin(e.target.value)} style={{ ...inputStyle, marginTop: 10 }} />
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} style={cancelButtonStyle}>Cancel</button>
          <button disabled={loading} onClick={showReset ? handleReset : pinSet ? handleUnlock : handleSetPin} style={confirmButtonStyle}>
            {showReset ? 'Reset & Unlock' : pinSet ? 'Unlock' : 'Set PIN'}
          </button>
        </div>

        {pinSet && !showReset && (
          <div onClick={() => { setShowReset(true); setPin(''); setConfirmPin(''); }} style={{ marginTop: 14, fontSize: 12, color: 'var(--accent-blue)', cursor: 'pointer', textAlign: 'center' }}>
            Forgot your PIN?
          </div>
        )}
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', marginBottom: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: 14 };
const cancelButtonStyle: React.CSSProperties = { padding: '9px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const confirmButtonStyle: React.CSSProperties = { padding: '9px 20px', background: 'var(--accent-blue)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };

export default VaultUnlockModal;
